import { Suspense, type ComponentType, type ReactNode } from 'react';
import { Outlet } from 'react-router';

// Deep imports on purpose: the pages barrel is only ever reached through React.lazy, and the
// frames must render from the entry chunk without pulling a page in.
import { ReelsCreatorBrandsFrame } from '@/pages/ReelsCreator/ReelsCreatorBrandsFrame';
import { ReelsCreatorHomeFrame } from '@/pages/ReelsCreator/ReelsCreatorHomeFrame';
import { ReelsCreatorSettingsFrame } from '@/pages/ReelsCreator/ReelsCreatorSettingsFrame';
import { ReelsWizardFrame } from '@/pages/ReelsCreator/ReelsWizardFrame';
import { ReelsCreatorRouteFallback } from './ReelsCreatorRouteFallback';
import { ReelsCreatorRouteGuard } from './ReelsCreatorRouteGuard';
import { useReelsRouteKind, type ReelsRouteKind } from './reelsRouteKind';

const FRAMES: Record<ReelsRouteKind, ComponentType<{ children: ReactNode }>> = {
  home: ReelsCreatorHomeFrame,
  brands: ReelsCreatorBrandsFrame,
  settings: ReelsCreatorSettingsFrame,
  wizard: ReelsWizardFrame,
};

/**
 * One persistent frame above the guard and the lazy pages: its entrance replays only when the
 * kind of page changes, and each kind's header lives here, so the guard check, the lazy load
 * and the page's own fetch swap the body underneath it and nothing else.
 */
export const ReelsCreatorRoute = () => {
  const kind = useReelsRouteKind();
  const Frame = FRAMES[kind];

  return (
    <div className="h-full min-h-0">
      <Frame>
        <ReelsCreatorRouteGuard fallback={<ReelsCreatorRouteFallback />}>
          <Suspense fallback={<ReelsCreatorRouteFallback />}>
            <Outlet />
          </Suspense>
        </ReelsCreatorRouteGuard>
      </Frame>
    </div>
  );
};
